import { useEffect, useRef, useCallback } from 'react';
import { useTenantQuery } from './useTenantQuery';
import { useAuthStore } from '@/store/auth.store';
import { useToastStore } from '@/store/toast.store';
import type { SecuritySettings } from '@/types/tenant-settings';

/**
 * Monitors user activity and logs the user out after the tenant-configured
 * idle timeout period. Shows a warning toast 2 minutes before logout.
 *
 * Implementation notes:
 * - Uses a periodic `setInterval` check (every 15 s) rather than a one-shot
 *   `setTimeout` for the logout deadline. Long setTimeouts can be silently
 *   throttled or canceled by some browsers when the tab is in the background;
 *   a periodic check is more robust because each tick is a fresh comparison.
 * - Tracks `lastActivityAt` via a ref so activity events don't have to clear
 *   and reschedule any timer — they just write a timestamp.
 * - Activity events use a 1-second throttle, which is cheap.
 * - Visibility changes (tab returning to foreground) run an immediate idle
 *   check, so a tab idle in the background can't escape the deadline.
 *
 * If `idleTimeoutMinutes` is 0 or missing, the hook is a no-op.
 */

const WARNING_BEFORE_LOGOUT_MS = 2 * 60 * 1000; // 2 minutes
const CHECK_INTERVAL_MS = 15 * 1000;            // 15 seconds
const ACTIVITY_EVENTS: (keyof DocumentEventMap)[] = [
  'mousedown',
  'keydown',
  'scroll',
  'touchstart',
  'mousemove',
];

export function useIdleTimeout() {
  const { data: security } = useTenantQuery<SecuritySettings>(
    ['tenant-settings', 'security'],
    '/tenant-settings/security',
    { staleTime: 300_000 },
  );

  const lastActivityRef = useRef<number>(Date.now());
  const warningShownRef = useRef<boolean>(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const logout = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    useAuthStore.getState().logout('idle');
    const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.href = `/login?reason=idle&returnUrl=${returnUrl}`;
  }, []);

  useEffect(() => {
    const idleMinutes = security?.idleTimeoutMinutes ?? 0;
    if (idleMinutes <= 0) return;

    const idleMs = idleMinutes * 60 * 1000;
    const warningMs = Math.max(idleMs - WARNING_BEFORE_LOGOUT_MS, 0);

    // Reset state for this configuration
    lastActivityRef.current = Date.now();
    warningShownRef.current = false;

    // ---- Activity tracking (throttled to avoid hot writes on rapid input) ----
    let lastWrite = 0;
    const onActivity = () => {
      const now = Date.now();
      if (now - lastWrite < 1000) return;
      lastWrite = now;
      lastActivityRef.current = now;
      // If user returned within the warning window, clear it so the next
      // warning fires fresh after they go idle again.
      warningShownRef.current = false;
    };

    ACTIVITY_EVENTS.forEach((event) =>
      document.addEventListener(event, onActivity, { passive: true }),
    );

    // ---- Periodic idle check ----
    const tick = () => {
      const idleFor = Date.now() - lastActivityRef.current;

      if (idleFor >= idleMs) {
        logout();
        return;
      }

      // Show warning when within the warning window (and we haven't shown
      // it yet for this idle stretch).
      if (warningMs > 0 && idleFor >= warningMs && !warningShownRef.current) {
        warningShownRef.current = true;
        const secondsLeft = Math.max(Math.ceil((idleMs - idleFor) / 1000), 1);
        const minutesLeft = Math.max(Math.ceil(secondsLeft / 60), 1);
        useToastStore.getState().addToast({
          title: 'Session Expiring',
          message: `You will be logged out in ${minutesLeft} minute${minutesLeft === 1 ? '' : 's'} due to inactivity.`,
          type: 'warning',
          duration: WARNING_BEFORE_LOGOUT_MS,
        });
      }
    };

    intervalRef.current = setInterval(tick, CHECK_INTERVAL_MS);

    // Run an immediate check when the tab becomes visible again, in case
    // the interval was throttled in the background.
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        tick();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      ACTIVITY_EVENTS.forEach((event) =>
        document.removeEventListener(event, onActivity),
      );
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [security?.idleTimeoutMinutes, logout]);
}
